import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Fuse from 'fuse.js';
import Drawer from '@material-ui/core/Drawer';
import Grid from '@material-ui/core/Grid';
import FiltersBar from 'components/FiltersBar';
import ManufacturerCard from './ManufacturerCard';
import SortingPanel from './SortingPanel';
import PageLoader from 'components/PageLoader';
import { fetchManufacturers } from 'store/supplies/suppliesOperations';
import suppliesActions from 'store/supplies/suppliesActions';
import { exportData, importData } from 'helpers/downloadFile';
import ArchiveDialog from '../ArchiveDialog';
import NoResults from '../NoResults';
import { impactedEntities, refetchSupplies } from '../../helpers/archive';

const searchOptions = {
  shouldSort: true,
  keys: ['publication408', 'designation', 'supplierCode', 'name'],
};

const Manufacturers = ({ onSelect, setSnackbar }) => {
  const dispatch = useDispatch();
  const [fuse, setFuse] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [sorting, setSorting] = useState({ sortBy: 'name', sortOrder: 'asc' });
  const [archivingManufacturer, setArchivingManufacturer] = useState(null);

  const orgSettings = useSelector((store) => store.personalProfile?.organization?.settings);
  const manufacturersData = useSelector((state) => state.supplies.manufacturers.data);
  const snackbar = useSelector((state) => state.supplies.manufacturers.snackBarInfo);
  const loading = useSelector((state) => state.supplies.manufacturers.loading);

  useEffect(() => {
    dispatch(fetchManufacturers());
  }, []);

  useEffect(() => {
    if (snackbar) {
      setSnackbar(snackbar);
      dispatch(suppliesActions.postManufactureError(false));
    }
  }, [snackbar]);

  const openDrawer = useCallback(
    () => () => {
      setDrawer({ isOpen: true });
    },
    []
  );

  const closeDrawer = useCallback(() => {
    setDrawer({ isOpen: false });
  }, []);

  const onUpdate = useCallback((values) => {
    setSorting(values);
    dispatch(fetchManufacturers(values));
    closeDrawer();
  }, []);

  const onSortingCancel = useCallback(() => {
    setSorting({
      sortBy: 'name',
      sortOrder: 'asc',
    });
    dispatch(fetchManufacturers());
    closeDrawer();
  }, []);

  const onArchived = useCallback((impact) => {
    refetchSupplies(dispatch, impactedEntities(impact).filter((entity) => entity !== 'manufacturers'));
    dispatch(fetchManufacturers(sorting));
  }, [sorting]);

  const onExportExcel = () => {
    exportData(
      'manufacturers/csv',
      {
        searchValue,
      },
      (type, text) => {
        setSnackbar({
          type,
          text,
        });
      }
    );
  };

  const onImportExcel = async (data) => {
    importData('manufacturers/csv', data, (type, text) => {
      setSnackbar({
        type,
        text,
      });
    }).then((e) => dispatch(fetchManufacturers()));
  };

  useMemo(() => {
    setFuse(new Fuse(manufacturersData, searchOptions));
  }, [manufacturersData]);

  const filteredManufacturers = fuse && searchValue ? fuse.search(searchValue) : manufacturersData;

  return loading ? (
    <PageLoader loading>
      <div style={{ height: 100 }} />
    </PageLoader>
  ) : (
    <>
      <FiltersBar
        openDrawer={openDrawer}
        updateSearchValue={setSearchValue}
        isDynamicSearch={true}
        hasImport={true}
        hasExport={true}
        onExportData={onExportExcel}
        onImportData={onImportExcel}
      />
      <Grid container spacing={2}>
        {filteredManufacturers.map((m) => (
          <Grid key={m._id} item xs={12} md={6} lg={3}>
            <ManufacturerCard
              data={m}
              onArchive={setArchivingManufacturer}
              onClick={() => onSelect(m)}
              orgSettings={orgSettings}
            />
          </Grid>
        ))}
      </Grid>
      {!filteredManufacturers.length && <NoResults />}
      {drawer && (
        <Drawer anchor="right" open={drawer.isOpen}>
          <SortingPanel
            closeDrawer={closeDrawer}
            onCancel={onSortingCancel}
            sorting={sorting}
            onUpdate={onUpdate}
            orgSettings={orgSettings}
          />
        </Drawer>
      )}
      {archivingManufacturer && (
        <ArchiveDialog
          entity="manufacturers"
          item={archivingManufacturer}
          onClose={() => setArchivingManufacturer(null)}
          onDone={onArchived}
          setSnackbar={setSnackbar}
        />
      )}
    </>
  );
};

Manufacturers.defaultProps = {
  onSelect: () => {},
};
export default Manufacturers;
